import * as React from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FileUpload from '@cloudscape-design/components/file-upload';
import FormField from '@cloudscape-design/components/form-field';
import { useState } from 'react';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';
import constants from '../constants.js';

const BasicDecrypt = () => {
  const [visible, setVisible] = React.useState(false);
  const [submitvalue, setSubmitvalue] = useState('Remove Encryption');
  const [notify, setNotify] = useState(null);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [totalsize, setTotalsize] = useState(0);

  const toggleModal = React.useCallback(() => {
    setVisible(true);
    setSubmitvalue('Remove Encryption');
    setNotify('');
    setError('');
    setSelectedFile(null);
    setTotalsize(0);
  }, []);

  const onClickFooterButton = () => {
    setVisible(false);
    setSubmitvalue('Remove Encryption');
    setNotify('');
    setError('');
    setSelectedFile(null);
    setTotalsize(0);
  };

  async function handleInputChange(e) {
    const files = e.detail.value;
    if (files.length === 0) {
      setSelectedFile(null);
      setTotalsize(0);
      return;
    }
    if (files.length > 1) {
      setNotify('Please select only one PDF file at a time.');
      setError('error');
      return;
    }
    const file = files[0];
    if (file.type !== constants.pdfFileType) {
      setNotify(constants.onlyPdfAllowed);
      setError('error');
      return;
    }
    setSelectedFile(file);
    setTotalsize(file.size / 1000000);
    setNotify('');
    setError(null);
  }

  async function RemoveEncryption() {
    if (!selectedFile) {
      setNotify('Please select a PDF file first.');
      setError('error');
      return;
    }
    setSubmitvalue(<Spinner />);
    try {
      const url = URL.createObjectURL(selectedFile);
      window.open(url, '_blank');
      addUsage('AWESOME PDF TOOLS', '1.0', 1, window.location.href);
      setSubmitvalue('Instructions Shown');
      setNotify('PDF opened in new tab. Use Ctrl+P (Cmd+P on Mac) → "Save as PDF" → Download.');
      setTimeout(() => {
        setVisible(false);
        setSelectedFile(null);
        setTotalsize(0);
      }, 5000);
    } catch (error) {
      console.error('Error opening PDF:', error);
      setNotify('Failed to open PDF. Please try manually.');
      setError('error');
      setSubmitvalue('Remove Encryption');
    }
  }

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        <div className="service-card-icon">
          <span>🔓</span>
        </div>
        <h3 className="service-card-title">Remove Encryption</h3>
        <p className="service-card-desc">Remove basic encryption from PDF files for merging</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={() => {
          setVisible(false);
          setSelectedFile(null);
        }}
        visible={visible}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onClickFooterButton}>
                Cancel
              </Button>
              {totalsize < 2000 && totalsize > 0 && selectedFile && !error ? (
                <Button variant="primary" onClick={RemoveEncryption}>
                  {submitvalue}
                </Button>
              ) : (
                <Button disabled variant="primary">
                  {totalsize > 2000 ? 'File Too Large' : submitvalue}
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
        header="Remove Basic Encryption"
      >
        <FormField
          errorText={notify}
          label="Upload encrypted PDF"
          description="Opens PDF for manual decryption via Print → Save as PDF"
        >
          <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '16px' }}>
            <strong>How it works:</strong>
            <ol style={{ marginLeft: '20px', marginTop: '10px' }}>
              <li>Upload your encrypted PDF</li>
              <li>Click "Remove Encryption" - PDF opens in new tab</li>
              <li>Press Ctrl+P (Cmd+P on Mac) → "Save as PDF"</li>
              <li>Upload the saved file to the appropriate PDF tool</li>
            </ol>
          </div>
          <FileUpload
            onChange={handleInputChange}
            value={[]}
            i18nStrings={{
              uploadButtonText: () => 'Choose file',
              dropzoneText: () => 'Drop file to upload',
              removeFileAriaLabel: () => 'Remove file',
              errorIconAriaLabel: 'Error',
            }}
            showFileLastModified
            showFileSize
            constraintText="Maximum file size: 2GB"
            accept=".pdf"
          />
        </FormField>
        {selectedFile && (
          <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            <div>
              <strong>Selected:</strong> {selectedFile.name}
            </div>
            <div>
              <strong>Size:</strong> {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </div>
          </div>
        )}
        <div style={{ fontSize: '0.9em', color: '#666' }}>
          <strong>Note:</strong> This feature removes basic encryption only. Password-protected PDFs cannot be
          processed. After decryption, use the downloaded file in the other PDF tools.
        </div>
      </Modal>
    </div>
  );
};

export default BasicDecrypt;
